import React from 'react';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-policy-page">
      <div className="privacy-hero">
        <div className="container">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-date">Last Updated: December 10, 2025</p>
        </div>
      </div>

      <div className="privacy-content">
        <div className="container">
          <section className="policy-section">
            <h2>Introduction</h2>
            <p>
              This Privacy Policy applies to all mobile applications developed by CreateMyApp, Tarpinder Singh Grewal and Insight Business Solutions, including but not limited to:
            </p>
            <ul className="app-list">
              <li><strong>Nitnem (With Audio)</strong> - Daily Sikh prayers with audio playback</li>
              <li><strong>Sukhmani Sahib</strong> - Complete Sukhmani Sahib path with translations</li>
              <li><strong>Juttikut</strong> - A simple fun game</li>
              <li>And other applications published under these names</li>
            </ul>
            <p>
              We respect your privacy and are committed to protecting it. Our applications do not collect, store, or share any personal information beyond what is required by the underlying frameworks and platforms used to deliver the apps.
            </p>
          </section>

          <section className="policy-section">
            <h2>Information We Collect</h2>
            <ul>
              <li>
                <strong>No direct collection:</strong> Our applications do not collect, store, or process any personal data from users. We do not track your usage, location, or any personally identifiable information.
              </li>
              <li>
                <strong>Audio and Content:</strong> Apps like Nitnem (With Audio) and Sukhmani Sahib provide audio and text content that is stored locally on your device. No data about what you read or listen to is transmitted to our servers.
              </li>
              <li>
                <strong>Offline Functionality:</strong> All our apps work completely offline. No internet connection is required for core functionality.
              </li>
              <li>
                <strong>Platform requirements:</strong> Our apps may rely on information automatically handled by:
                <ul>
                  <li>.NET MAUI framework (for app functionality and compatibility)</li>
                  <li>Flutter framework (for app functionality and compatibility)</li>
                  <li>Google Play services (for distribution, updates, crash reporting, and compliance with Google Play policies)</li>
                  <li>Apple App Store services (for iOS apps distribution and updates)</li>
                </ul>
              </li>
            </ul>
            <p>Any data handled by these platforms is subject to their respective privacy policies.</p>
          </section>

          <section className="policy-section">
            <h2>How Information Is Used</h2>
            <ul>
              <li>Information required by Google Play, Apple App Store, .NET MAUI, or Flutter is used solely to ensure the apps run correctly, are distributed securely, and remain compliant with platform requirements.</li>
              <li>We do not use any information for analytics, advertising, marketing, or user tracking.</li>
              <li>Your app preferences and settings are stored locally on your device only.</li>
              <li>Audio playback and text display features operate entirely offline without any data transmission.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>Sharing of Information</h2>
            <ul>
              <li>We do not share, sell, rent, or disclose any user information to third parties.</li>
              <li>We do not have any third-party analytics, advertising networks, or tracking services integrated into our apps.</li>
              <li>Any data processed by Google Play, Apple App Store, .NET MAUI, or Flutter is managed according to their own privacy practices.</li>
              <li>All content (prayers, audio, text) is stored locally on your device and never transmitted to external servers.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>Advertising and Monetization</h2>
            <p>
              <strong>Nitnem (With Audio)</strong> and <strong>Sukhmani Sahib</strong> do not contain any advertisements, third-party advertising networks, or monetization SDKs. These apps are completely ad-free.
            </p>
            <p>
              Other applications may contain advertisements, which will be clearly disclosed in their respective app descriptions and privacy policies.
            </p>
          </section>

          <section className="policy-section">
            <h2>Data Retention and Deletion</h2>
            <p>
              <strong>Our Apps:</strong> We do not collect or retain any user data on our servers. All app data is stored locally on your device only.
            </p>
            <p>
              <strong>Platform Data:</strong> Google Play Services, .NET MAUI framework, and Flutter framework may collect diagnostic and crash data according to their own retention policies. This data is managed by Google and Microsoft respectively:
            </p>
            <ul>
              <li><strong>Google Play Services:</strong> Refer to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a></li>
              <li><strong>Flutter:</strong> Refer to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a></li>
              <li><strong>.NET MAUI:</strong> Refer to <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer">Microsoft's Privacy Statement</a></li>
            </ul>
            <p>
              <strong>Deletion:</strong> To delete all app data, simply uninstall the application from your device. This will remove all locally stored preferences and content.
            </p>
          </section>

          <section className="policy-section">
            <h2>Data Security</h2>
            <p>
              Since our applications do not collect or transmit any personal information, there is minimal security risk. All app data is stored locally on your device and is protected by your device's security features.
            </p>
          </section>

          <section className="policy-section">
            <h2>Regional Privacy Rights</h2>
            <p>
              While we do not collect personal data, we recognize the privacy rights of users worldwide:
            </p>
            <ul>
              <li><strong>GDPR (European Union):</strong> Since we do not collect personal data, GDPR obligations do not apply to our data practices. However, platform providers (Google, Microsoft) may collect diagnostic data under their own GDPR compliance.</li>
              <li><strong>CCPA (California):</strong> We do not sell personal information. We do not collect personal information for advertising or analytics purposes.</li>
              <li><strong>PIPEDA (Canada):</strong> As a Canadian developer, we comply with Canadian privacy laws. No personal information is collected or processed by our applications.</li>
            </ul>
            <p>
              For any privacy-related requests or concerns, please contact us using the information provided below.
            </p>
          </section>

          <section className="policy-section">
            <h2>Children's Privacy</h2>
            <p>
              Our applications do not knowingly collect or process any personal information from children or any users. The apps are designed to be safe for all ages and can be used by children without any privacy concerns.
            </p>
          </section>

          <section className="policy-section">
            <h2>Third-Party Links</h2>
            <p>
              Our applications do not contain any third-party links, advertisements, or integrations that could compromise your privacy.
            </p>
          </section>

          <section className="policy-section">
            <h2>Permissions</h2>
            <p>
              Our apps may request the following device permissions:
            </p>
            <ul>
              <li><strong>Storage:</strong> To save app preferences and audio files locally on your device</li>
              <li><strong>Audio:</strong> To play prayer audio in apps like Nitnem (With Audio) and Sukhmani Sahib</li>
            </ul>
            <p>
              These permissions are used solely for app functionality and do not involve any data collection or transmission.
            </p>
          </section>

          <section className="policy-section">
            <h2>Your Rights and Choices</h2>
            <p>
              Since we do not collect any personal information, there is no data to access, modify, or delete from our servers. You have complete control over all app data stored locally on your device.
            </p>
            <p>
              <strong>Data Access:</strong> All your app data is stored on your device. You can access it through the app's settings and preferences.
            </p>
            <p>
              <strong>Data Deletion:</strong> To delete all app data:
            </p>
            <ul>
              <li>Clear app data through your device's Settings → Apps → [App Name] → Storage → Clear Data</li>
              <li>Or uninstall the application to remove all data</li>
            </ul>
            <p>
              <strong>Opt-Out:</strong> You can opt out of Google Play's personalized ads and diagnostics through your device settings, though our apps do not use these features.
            </p>
          </section>

          <section className="policy-section">
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy if required by app store policies or framework changes. Updates will be reflected here with a new effective date. Continued use of our applications after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="policy-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="contact-info">
              📧 <a href="mailto:info@createmyapp.ca">info@createmyapp.ca</a><br />
              📱 604-773-1594<br />
              📍 Surrey, BC, Canada
            </p>
          </section>

          <div className="back-home">
            <a href="/" className="back-button">← Back to Home</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
